import { useEffect, useRef, useState } from "react";
import { Animated, AppState, Easing, Platform } from "react-native";

const USE_NATIVE = Platform.OS !== "web";

// ---------------------------------------------------------------------------
// AppState helper — true iff the app is foregrounded. Used by the loop hooks
// below so animations stop burning CPU/GPU while the screen is hidden
// (expo-router stack-pushes don't unmount; backgrounded apps keep
// useNativeDriver loops running until explicit stop).
// ---------------------------------------------------------------------------

function useAppStateActive(): boolean {
    const [active, setActive] = useState(() => AppState.currentState === "active");
    useEffect(() => {
        const sub = AppState.addEventListener("change", (state) => {
            setActive(state === "active");
        });
        return () => sub.remove();
    }, []);
    return active;
}

// ---------------------------------------------------------------------------
// Looping 0 -> 1 Animated.Value (extracted from AiVerificationAnimation).
// Pauses while the app is backgrounded.
// ---------------------------------------------------------------------------

export function useLoopValue(duration: number) {
    const v = useRef(new Animated.Value(0)).current;
    const active = useAppStateActive();
    useEffect(() => {
        if (!active) {
            return;
        }
        const a = Animated.loop(
            Animated.timing(v, {
                toValue: 1,
                duration,
                easing: Easing.linear,
                useNativeDriver: USE_NATIVE
            })
        );
        a.start();
        return () => a.stop();
    }, [v, duration, active]);
    return v;
}

// ---------------------------------------------------------------------------
// Oscillating min <-> max Animated.Value. Pauses while backgrounded.
// ---------------------------------------------------------------------------

export function usePulseValue(duration: number, min: number, max: number) {
    const v = useRef(new Animated.Value(min)).current;
    const active = useAppStateActive();
    useEffect(() => {
        if (!active) {
            return;
        }
        const a = Animated.loop(
            Animated.sequence([
                Animated.timing(v, {
                    toValue: max,
                    duration: duration / 2,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: USE_NATIVE
                }),
                Animated.timing(v, {
                    toValue: min,
                    duration: duration / 2,
                    easing: Easing.inOut(Easing.ease),
                    useNativeDriver: USE_NATIVE
                })
            ])
        );
        a.start();
        return () => a.stop();
    }, [v, duration, min, max, active]);
    return v;
}

// ---------------------------------------------------------------------------
// Fade in on mount with a delay. (One-shot — no AppState gating needed.)
// ---------------------------------------------------------------------------

export function useFadeIn(delay: number, duration: number) {
    const opacity = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        const a = Animated.sequence([
            Animated.delay(delay),
            Animated.timing(opacity, {
                toValue: 1,
                duration,
                easing: Easing.out(Easing.ease),
                useNativeDriver: USE_NATIVE
            })
        ]);
        a.start();
        return () => a.stop();
    }, [opacity, delay, duration]);
    return opacity;
}

// ---------------------------------------------------------------------------
// Staggered fade-in with slide up (for list items). One-shot.
// ---------------------------------------------------------------------------

export function useStaggerFadeIn(index: number, delayPerItem: number) {
    const opacity = useRef(new Animated.Value(0)).current;
    const translateY = useRef(new Animated.Value(12)).current;
    useEffect(() => {
        const a = Animated.sequence([
            Animated.delay(index * delayPerItem),
            Animated.parallel([
                Animated.timing(opacity, {
                    toValue: 1,
                    duration: 300,
                    easing: Easing.out(Easing.ease),
                    useNativeDriver: USE_NATIVE
                }),
                Animated.timing(translateY, {
                    toValue: 0,
                    duration: 300,
                    easing: Easing.out(Easing.ease),
                    useNativeDriver: USE_NATIVE
                })
            ])
        ]);
        a.start();
        return () => a.stop();
    }, [opacity, translateY, index, delayPerItem]);
    return { opacity, translateY };
}
